import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Phone, MapPin, Clock, Mail } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { CONTACT } from "@/constants/testIds";
import { useSettings, telHref } from "@/context/SettingsContext";
import { Reveal, MaskedLine, EASE } from "@/components/motion/Reveal";
import useSEO from "@/lib/useSEO";

export default function Contatti() {
  useSEO({ title: "Contatti", description: "Contatta lo studio Giovanna Matino Interior a Pomigliano d'Arco (Napoli): telefono, email, WhatsApp e form di richiesta." });
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Messaggio inviato. Ti risponderemo al più presto.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Errore, riprova");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="page-contatti">
      <section className="container-x pt-10 pb-16">
        <motion.span className="eyebrow block" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>Contatti</motion.span>
        <h1 className="font-serif text-5xl md:text-7xl leading-[1.05] mt-6 max-w-4xl">
          <MaskedLine delay={0.15}>Scrivimi.</MaskedLine>
          <MaskedLine delay={0.32}><em className="text-gold font-light">Ti risponderò presto.</em></MaskedLine>
        </h1>
      </section>

      <section className="container-x pb-24 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
        <div className="space-y-10">
          <div>
            <div className="eyebrow mb-3">Studio</div>
            <div className="flex gap-4 items-start">
              <MapPin className="text-gold mt-1" size={20} />
              <div className="text-ink leading-relaxed">
                {settings.address_line1}<br/>
                {settings.address_line2}<br/>
                <span className="text-taupe text-sm">Città Metropolitana di Napoli</span>
              </div>
            </div>
          </div>
          <div>
            <div className="eyebrow mb-3">Telefono</div>
            <div className="flex gap-4 items-start">
              <Phone className="text-gold mt-1" size={20} />
              <a href={telHref(settings.phone)} className="text-ink hover:text-gold text-lg" data-testid="contact-phone-link">{settings.phone}</a>
            </div>
          </div>
          <div>
            <div className="eyebrow mb-3">Email</div>
            <div className="flex gap-4 items-start">
              <Mail className="text-gold mt-1" size={20} />
              <a href={`mailto:${settings.email}`} className="text-ink hover:text-gold" data-testid="contact-email-link">{settings.email}</a>
            </div>
          </div>
          <div>
            <div className="eyebrow mb-3">Orari</div>
            <div className="flex gap-4 items-start">
              <Clock className="text-gold mt-1" size={20} />
              <div>
                <div className="text-ink" data-testid="contact-opening-hours">{settings.opening_hours}</div>
              </div>
            </div>
          </div>
          <div className="pt-4">
            <div className="eyebrow mb-3">Aree Servite</div>
            <div className="text-taupe font-light" data-testid="contact-service-areas">{settings.service_areas}</div>
          </div>
        </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-7">
        <form onSubmit={onSubmit} className="bg-white border border-hair p-8 md:p-10 space-y-6" data-testid={CONTACT.form}>
          <div>
            <Label className="text-xs tracking-[0.14em] uppercase">Nome</Label>
            <Input required value={form.name} onChange={set("name")} data-testid={CONTACT.name} className="rounded-none mt-2" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs tracking-[0.14em] uppercase">Email</Label>
              <Input type="email" required value={form.email} onChange={set("email")} data-testid={CONTACT.email} className="rounded-none mt-2" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.14em] uppercase">Telefono</Label>
              <Input value={form.phone} onChange={set("phone")} data-testid={CONTACT.phone} className="rounded-none mt-2" />
            </div>
          </div>
          <div>
            <Label className="text-xs tracking-[0.14em] uppercase">Oggetto</Label>
            <Input value={form.subject} onChange={set("subject")} data-testid={CONTACT.subject} className="rounded-none mt-2" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.14em] uppercase">Messaggio</Label>
            <Textarea rows={6} required value={form.message} onChange={set("message")} data-testid={CONTACT.message} className="rounded-none mt-2" />
          </div>
          <button type="submit" disabled={sending} className="btn-ink w-full md:w-auto disabled:opacity-60" data-testid={CONTACT.submit}>
            {sending ? "Invio…" : "Invia Messaggio"}
          </button>
        </form>
        </Reveal>
      </section>

      <section className="w-full">
        <iframe
          title="Mappa studio"
          src="https://maps.google.com/maps?q=Via%20Carmine%20Guadagno%20160%20Pomigliano%20d'Arco&t=&z=15&ie=UTF8&iwloc=&output=embed"
          className="w-full h-[420px] grayscale border-t border-hair"
          loading="lazy"
        />
      </section>
    </div>
  );
}
